import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  ArrowLeft,
  AlertCircle,
  ExternalLink,
  CheckCircle2,
  MessageSquare,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SavingsEstimate, SavingsSummary } from '../components/strategy/SavingsEstimate';

const API = process.env.REACT_APP_BACKEND_URL;

const StrategyReport = () => {
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    per_topic_feedback: [],
    general_feedback: ''
  });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    loadReport();
    
    // Check if feedback form should be shown
    if (searchParams.get('feedback') === 'true') {
      setShowFeedbackForm(true);
    }
  }, [reportId, user, navigate, searchParams]);

  const loadReport = async () => {
    try {
      const response = await axios.get(
        `${API}/api/strategy_interview/reports/${reportId}`,
        { withCredentials: true }
      );
      
      setReport(response.data);
      
      // Initialize feedback data structure
      const topics = response.data.report_content?.topics || [];
      setFeedbackData({
        per_topic_feedback: topics.map(topic => ({
          topic_id: topic.topic_id,
          status: '',
          cpa_notes: ''
        })),
        general_feedback: ''
      });
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Failed to load report. Please try again.');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/api/strategy_interview/reports/${reportId}/pdf`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Tax_Strategy_Report_v${report.version}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleFeedbackChange = (topicIndex, field, value) => {
    setFeedbackData(prev => ({
      ...prev,
      per_topic_feedback: prev.per_topic_feedback.map((item, idx) => 
        idx === topicIndex ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleSubmitFeedback = async () => {
    // Validate at least one topic has feedback
    const hasAnyFeedback = feedbackData.per_topic_feedback.some(f => f.status !== '');
    
    if (!hasAnyFeedback && !feedbackData.general_feedback) {
      toast.error('Please provide feedback for at least one topic or add general notes');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await axios.post(
        `${API}/api/strategy_interview/reports/${reportId}/feedback`,
        feedbackData,
        { withCredentials: true }
      );
      
      toast.success('Feedback submitted successfully!');
      setShowFeedbackForm(false);
      loadReport(); // Reload to show feedback submitted status
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      const message = error.response?.data?.detail || 'Failed to submit feedback';
      toast.error(message);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const { report_content, version, generated_at, rag_sources_used, interview_snapshot } = report;
  const { header, snapshot_summary, topics, disclaimer_text, disclaimer_variant, savings_summary } = report_content;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            
            <Badge variant="outline" className="flex items-center gap-2">
              <FileText className="h-3 w-3" />
              Version {version}
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Header Card */}
        <Card className="mb-6">
          <CardHeader className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {header.report_title}
                </CardTitle>
                <p className="text-sm text-gray-600 mb-1">
                  {header.header_for_line}
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                  <span>Prepared for: {header.user_name}</span>
                  <span>•</span>
                  <span>Tax Year: {header.tax_year}</span>
                  <span>•</span>
                  <span>Generated: {new Date(header.generation_date).toLocaleDateString()}</span>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2 self-start shrink-0"
                data-testid="download-pdf-btn"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
              >
                <Download className="h-4 w-4" />
                {downloadingPdf ? 'Downloading...' : 'Download PDF'}
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 whitespace-pre-line">{header.intro_text}</p>
            </div>
            
            {snapshot_summary && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-1">Your Situation Summary</p>
                <p className="text-sm text-blue-800">{snapshot_summary}</p>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-gray-900">Interview Completeness</p>
                <p className="text-gray-600">
                  {interview_snapshot.questions_answered} of {interview_snapshot.questions_allocated} questions answered 
                  ({interview_snapshot.completeness_at_generation.toFixed(1)}%)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Illustrative combined savings */}
        {savings_summary && (
          <div className="mb-6">
            <SavingsSummary summary={savings_summary} />
          </div>
        )}

        {/* Topics */}
        <div className="space-y-4 mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Discussion Topics ({topics.length})
          </h2>
          
          {topics.map((topic, index) => (
            <Card key={topic.topic_id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg font-semibold text-gray-900 mb-2">
                      {topic.topic_name}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  {/* Relevance */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Why This May Be Relevant</p>
                    <p className="text-sm text-gray-600">{topic.relevance_explanation}</p>
                  </div>
                  
                  {/* Illustrative savings estimate */}
                  <SavingsEstimate estimate={topic.savings_estimate} topicId={topic.topic_id} />

                  {/* Code References */}
                  {topic.code_references && topic.code_references.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">IRS References</p>
                      <div className="space-y-1">
                        {topic.code_references.map((ref, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                            <ExternalLink className="h-3 w-3 text-gray-400 shrink-0" />
                            <span>{ref}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Question for CPA */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <p className="text-sm font-medium text-purple-900 mb-1">Question to Ask Your Advisor</p>
                    <p className="text-sm text-purple-800">{topic.question_for_cpa}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* RAG Sources */}
        {rag_sources_used && rag_sources_used.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ExternalLink className="h-5 w-5 text-gray-600" />
                IRS Publications Referenced
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {rag_sources_used.map((source, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{source.publication}</p>
                      <p className="text-xs text-gray-600">{source.section}</p>
                    </div>
                    {source.url && (
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <CardTitle className="text-base font-semibold text-amber-900">
                  {disclaimer_variant === 'self_directed' ? 'Important Notice — Educational Content' : 'Important Notice'}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-900 whitespace-pre-line">{disclaimer_text}</p>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="mt-8 space-y-4">
          {/* Feedback Section */}
          {!report.feedback_submitted && !showFeedbackForm && (
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="py-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Had Your CPA Meeting?
                    </h3>
                    <p className="text-sm text-blue-800 mb-4">
                      After meeting with your tax advisor, log what you discussed to track
                      which topics were applied to your tax strategy.
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowFeedbackForm(true)}
                    className="flex items-center gap-2 self-start shrink-0"
                  >
                    <MessageSquare className="h-4 w-4" />
                    Add Meeting Notes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Feedback Form */}
          {showFeedbackForm && (
            <Card className="border-2 border-purple-200">
              <CardHeader className="border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>CPA Meeting Feedback</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      Log what happened with each discussion topic during your meeting
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFeedbackForm(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="pt-6 space-y-6">
                {/* Per-topic feedback */}
                {topics.map((topic, idx) => (
                  <div key={topic.topic_id} className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      {idx + 1}. {topic.topic_name}
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          What happened with this topic?
                        </label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={feedbackData.per_topic_feedback[idx]?.status || ''}
                          onChange={(e) => handleFeedbackChange(idx, 'status', e.target.value)}
                        >
                          <option value="">Select status...</option>
                          <option value="discussed_applied">Discussed & Applied to my strategy</option>
                          <option value="discussed_not_applicable">Discussed & CPA said not applicable</option>
                          <option value="not_discussed">Not discussed</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notes (optional)
                        </label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="2"
                          placeholder="What did your CPA say about this topic?"
                          value={feedbackData.per_topic_feedback[idx]?.cpa_notes || ''}
                          onChange={(e) => handleFeedbackChange(idx, 'cpa_notes', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* General feedback */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    General Meeting Notes (optional)
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Any additional notes about your CPA meeting..."
                    value={feedbackData.general_feedback}
                    onChange={(e) => setFeedbackData(prev => ({...prev, general_feedback: e.target.value}))}
                  />
                </div>
                
                <div className="flex items-center gap-3 pt-4">
                  <Button
                    onClick={handleSubmitFeedback}
                    disabled={submittingFeedback}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowFeedbackForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Feedback already submitted */}
          {report.feedback_submitted && (
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="py-6">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-green-900">
                      Feedback Submitted
                    </h3>
                    <p className="text-sm text-green-800">
                      Meeting notes submitted on {new Date(report.feedback_submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Navigation buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/strategy-advisor')}
              className="flex items-center gap-2"
            >
              Edit Interview Answers
            </Button>
            
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/strategy-advisor/reports')}
              >
                View All Reports
              </Button>
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                Return to Dashboard
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyReport;
